import JSZip from 'jszip';
import { useAuthToken } from '../../../../hooks/AuthHook.jsx';
import { accounting_urls } from '../../../../api/endpoints.js';

const BATCH_SIZE = 100;
// UTF-8 BOM for Excel compatibility with emojis and special characters
const UTF8_BOM = "\uFEFF";

export const useJournalStatusReport = () => {
    const authToken = useAuthToken();

    const generateReport = async ({ country, platform, references, onProgress, onStatus }) => {
        try {
            const totalBatches = Math.ceil(references.length / BATCH_SIZE);
            const zip = new JSZip();

            let allAnalyzedRows = [];
            let allNotFoundRows = [];
            let analyzedHeader = "";
            let notFoundHeader = "";

            for (let i = 0; i < totalBatches; i++) {
                const batch = references.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE);
                if (onStatus) onStatus(`Procesando lote ${i + 1} de ${totalBatches}...`);

                const response = await fetch(accounting_urls.erp.reports.journal_status(country), {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        platform: platform,
                        references: batch
                    })
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.description || "Error al generar el reporte");
                }

                const blob = await response.blob();
                const batchZip = await JSZip.loadAsync(blob);

                if (batchZip.file("analyzed.csv")) {
                    const content = await batchZip.file("analyzed.csv").async("string");
                    // Remove BOM if present from API response before processing
                    const cleanContent = content.replace(/^\uFEFF/, "");
                    const lines = cleanContent.trim().split("\n");
                    if (lines.length > 0) {
                        if (!analyzedHeader) analyzedHeader = lines[0];
                        allAnalyzedRows.push(...lines.slice(1));
                    }
                }

                if (batchZip.file("references_not_found.csv")) {
                    const content = await batchZip.file("references_not_found.csv").async("string");
                    // Remove BOM if present from API response before processing
                    const cleanContent = content.replace(/^\uFEFF/, "");
                    const lines = cleanContent.trim().split("\n");
                    if (lines.length > 0) {
                        if (!notFoundHeader) notFoundHeader = lines[0];
                        allNotFoundRows.push(...lines.slice(1));
                    }
                }

                if (onProgress) onProgress(((i + 1) / totalBatches) * 100);
            }

            if (onStatus) onStatus("Generando archivo final...");

            // Add UTF-8 BOM at the beginning for proper Excel emoji support
            if (analyzedHeader || allAnalyzedRows.length > 0) {
                const finalAnalyzedParams = UTF8_BOM + [analyzedHeader, ...allAnalyzedRows].join("\n");
                zip.file("analyzed.csv", finalAnalyzedParams);
            }

            if (notFoundHeader || allNotFoundRows.length > 0) {
                const finalNotFoundParams = UTF8_BOM + [notFoundHeader, ...allNotFoundRows].join("\n");
                zip.file("references_not_found.csv", finalNotFoundParams);
            }

            const content = await zip.generateAsync({ type: "blob" });
            const url = window.URL.createObjectURL(content);
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
            const filename = `journal_status_report_${country}_${timestamp}.zip`;

            return { url, filename };

        } catch (err) {
            console.error(err);
            throw err;
        }
    };

    return { generateReport };
};
